/**
 * 55 — take it with you.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  THE PROBLEM, IN THE BAND'S OWN WORDS: "we go camping".
 *
 *  Two hours north of the city there is no LTE, no wifi, and a presigned R2
 *  URL that expired at lunchtime. Every other screen in this app assumes the
 *  vault is one round trip away. This is the screen that assumes it isn't.
 *
 *  So we do not save a URL. A presigned URL is a bearer token with an hour to
 *  live — saving one would produce a beautifully designed 403 at the campsite.
 *  We save the BYTES, keyed by take, and serve them back from a URL that never
 *  changes: /offline/audio/<take_id>.mp3, handled by the service worker.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * WHY THE CACHE API AND NOT INDEXEDDB
 * A media element cannot play an IndexedDB blob without us first reading the
 * whole thing into memory and minting an object URL — a 57 MB ArrayBuffer on a
 * phone, per track, and a URL that dies on reload. The Cache API stores a
 * Response and the service worker hands it straight back to <audio>, range
 * requests and all, streaming off disk. That is what it is for.
 *
 * WHAT 06_FRONTEND_SPEC MEANT BY "DO NOT CACHE AUDIO"
 * It meant: never cache audio AUTOMATICALLY. An app that quietly hoards every
 * take someone presses play on will fill a 64 GB phone and then get the whole
 * origin evicted — shell included — and the app just stops working one day for
 * no reason anybody can explain. That rule stands. This is the other thing: a
 * download the user asked for, by name, with the size on the button, and a
 * Remove next to it. Different feature. See the same note in sw.js.
 *
 * iOS REALITY, SAID OUT LOUD
 * Safari evicts Cache API data after about seven days of not opening the app,
 * and sooner under storage pressure. We ask for an exemption with
 * navigator.storage.persist(), and Safari is entitled to say no — it usually
 * does, unless the app is installed to the home screen. So we never trust a
 * flag: isDownloaded() asks the CACHE whether the bytes are really there. A
 * silently evicted file must show up as "not downloaded" on the screen, not as
 * a mystery failure at the moment he presses play in the woods.
 */

import { h, clear, icon, toast, fmtTime, sheet } from './ui';
import './offline.css';

/* ------------------------------------------------------------------ storage */

/**
 * NOT versioned with the shell cache. A deploy must never delete somebody's
 * downloads — they might be standing in a field. sw.js knows this name and
 * keeps it alive across activations for exactly the same reason.
 */
const CACHE = '55-offline-v1';

/** The stable local URLs. The presigned URL changes on every mint; these never
 *  do, which is the entire trick. */
const audioKey = (takeId: string) => `/offline/audio/${encodeURIComponent(takeId)}.mp3`;
const peaksKey = (takeId: string) => `/offline/peaks/${encodeURIComponent(takeId)}.json`;
const metaKey = (takeId: string) => `/offline/meta/${encodeURIComponent(takeId)}.json`;

/** Cache Storage is unavailable on http:// and in some private modes. Every
 *  entry point degrades to "nothing is downloaded" rather than throwing. */
function cacheOk(): boolean {
  return typeof caches !== 'undefined';
}

/**
 * What we remember about a download, so the offline screen can list what you
 * have WITHOUT the network — which is the only time you actually need it.
 * It is a convenience, never the source of truth: the audio entry is.
 */
export interface SavedMeta {
  take_id: string;
  version_id?: string | null;
  song_id?: string | null;
  song_title?: string;
  name?: string;
  duration_s?: number | null;
  bytes?: number | null;
  saved_at: number;
}

/* --------------------------------------------------------------- the API */

/**
 * Is this take really on the phone?
 *
 * Asks the cache. Never a localStorage flag. iOS evicts, and a flag that says
 * "downloaded" about bytes that Safari threw away last Tuesday is worse than
 * useless — it is a promise the app breaks in the one place it can't recover.
 */
export async function isDownloaded(takeId: string): Promise<boolean> {
  if (!cacheOk()) return false;
  try {
    const cache = await caches.open(CACHE);
    return (await cache.match(audioKey(takeId))) !== undefined;
  } catch {
    return false;
  }
}

/** Every take currently on the phone, by id. Read from the cache, for the same
 *  reason as above. */
export async function downloadedTakeIds(): Promise<Set<string>> {
  const out = new Set<string>();
  if (!cacheOk()) return out;
  try {
    const cache = await caches.open(CACHE);
    for (const req of await cache.keys()) {
      const m = new URL(req.url).pathname.match(/^\/offline\/audio\/(.+)\.mp3$/);
      if (m?.[1]) out.add(decodeURIComponent(m[1]));
    }
  } catch {
    /* no cache, no downloads. Not an error. */
  }
  return out;
}

/** A stable local URL for a downloaded take, or null if it isn't on the phone.
 *
 *  This is what the player should try FIRST, falling back to the presigned URL:
 *
 *      const local = await offlineUrl(takeId);
 *      audio.src = local ?? info.url;
 *
 *  It is a plain same-origin path, so it survives a reload, an app restart, and
 *  a week in a tent. The service worker serves it out of the cache — including
 *  the byte-range requests Safari fires roughly fifteen times per play. */
export async function offlineUrl(takeId: string): Promise<string | null> {
  return (await isDownloaded(takeId)) ? audioKey(takeId) : null;
}

/**
 * The escape hatch, and the reason it exists:
 *
 * A media element's range requests are supposed to go through the service
 * worker. WebKit has a long history of routing media loads AROUND it, and if
 * that ever bites us on a real iPhone then offlineUrl() above resolves to a
 * 404 in the woods, which is the worst possible place to find out.
 *
 * This returns a blob: URL for the same cached bytes. Blob URLs are handled
 * inside the browser — no service worker, no network, native range support —
 * so they cannot fail that way. They do not survive a reload (mint a new one)
 * and they hold a reference until revoked, so use them as a fallback, not as
 * the default:
 *
 *      audio.src = (await offlineUrl(id)) ?? (await offlineBlobUrl(id)) ?? info.url;
 */
const blobUrls = new Map<string, string>();

export async function offlineBlobUrl(takeId: string): Promise<string | null> {
  const existing = blobUrls.get(takeId);
  if (existing) return existing;
  if (!cacheOk()) return null;
  try {
    const cache = await caches.open(CACHE);
    const hit = await cache.match(audioKey(takeId));
    if (!hit) return null;
    const url = URL.createObjectURL(await hit.blob());
    blobUrls.set(takeId, url);
    return url;
  } catch {
    return null;
  }
}

function revokeBlobUrl(takeId: string): void {
  const url = blobUrls.get(takeId);
  if (!url) return;
  URL.revokeObjectURL(url);
  blobUrls.delete(takeId);
}

/* ------------------------------------------------------------- downloading */

/** In-flight downloads, so a second tap cancels rather than doubles up. */
const inflight = new Map<string, AbortController>();

export function isDownloading(takeId: string): boolean {
  return inflight.has(takeId);
}

export function cancelDownload(takeId: string): void {
  inflight.get(takeId)?.abort();
}

export function cancelAllDownloads(): void {
  for (const ac of inflight.values()) ac.abort();
}

interface DownloadInfo {
  url: string;
  peaks_url: string | null;
  mime: string;
  filename: string;
  bytes: number | null;
  est_bytes: number | null;
  duration_s: number | null;
  version_id: string | null;
}

/** GET /api/takes/:id/download — a presigned URL, its peaks, and a real
 *  filename. Deliberately NOT /stream: asking /stream for the bytes counts as
 *  having listened, and saving a song for later is not listening to it. */
async function downloadInfo(takeId: string, signal: AbortSignal): Promise<DownloadInfo> {
  const res = await fetch(`/api/takes/${encodeURIComponent(takeId)}/download`, {
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw new Error('no download url');
  const j = (await res.json()) as Record<string, unknown>;
  return {
    url: String(j.url ?? ''),
    peaks_url: typeof j.peaks_url === 'string' ? j.peaks_url : null,
    mime: typeof j.mime === 'string' ? j.mime : 'audio/mpeg',
    filename: String(j.filename ?? 'song.mp3'),
    bytes: typeof j.bytes === 'number' ? j.bytes : null,
    est_bytes: typeof j.est_bytes === 'number' ? j.est_bytes : null,
    duration_s: typeof j.duration_s === 'number' ? j.duration_s : null,
    version_id: typeof j.version_id === 'string' ? j.version_id : null,
  };
}

/**
 * Save a take to the phone.
 *
 * `onProgress` is called with a REAL percentage, 0–100, computed from bytes
 * actually read off the wire against the Content-Length R2 gave us. It is not
 * an animation. If R2 declines to tell us the length (it always tells us, but
 * still) we report 0 until the bytes are safely in the cache and then 100 —
 * because a bar that crawls to 99% and sits there is a bar that lies.
 *
 * The bytes are piped STRAIGHT into the cache. They are never assembled into
 * one big ArrayBuffer, so a 57-minute jam costs a few kilobytes of RAM on the
 * way past instead of 55 MB of it on a phone that has none to spare.
 */
export async function downloadTake(
  takeId: string,
  onProgress?: (pct: number) => void,
  meta?: Omit<SavedMeta, 'take_id' | 'saved_at'>,
): Promise<void> {
  if (!cacheOk()) throw new Error('no cache storage');
  if (inflight.has(takeId)) return;

  const ac = new AbortController();
  inflight.set(takeId, ac);

  try {
    const cache = await caches.open(CACHE);
    const info = await downloadInfo(takeId, ac.signal);

    const res = await fetch(info.url, { signal: ac.signal });
    if (!res.ok || !res.body) throw new Error(`r2 said ${res.status}`);

    const total = Number(res.headers.get('content-length') ?? 0);
    let seen = 0;
    onProgress?.(0);

    const counted = res.body.pipeThrough(
      new TransformStream<Uint8Array, Uint8Array>({
        transform(chunk, ctrl) {
          seen += chunk.byteLength;
          // Honest, or silent. Never invented.
          if (total > 0) onProgress?.(Math.min(99, Math.floor((seen / total) * 100)));
          ctrl.enqueue(chunk);
        },
      }),
    );

    // We rebuild the Response so the cached entry carries exactly the headers
    // the service worker needs to answer a range request: a content-type Safari
    // will accept, a length, and accept-ranges. R2's own CORS response headers
    // are not our business once the bytes are ours.
    const headers = new Headers({
      'content-type': info.mime || 'audio/mpeg',
      'accept-ranges': 'bytes',
    });
    if (total > 0) headers.set('content-length', String(total));

    await cache.put(audioKey(takeId), new Response(counted, { status: 200, headers }));

    // The waveform, so the picture still draws with no signal. Its absence is a
    // flat grey box, not a failure — never let it sink the download.
    if (info.peaks_url) {
      try {
        const pk = await fetch(info.peaks_url, { signal: ac.signal });
        if (pk.ok) await cache.put(peaksKey(takeId), pk);
      } catch {
        /* no peaks offline. The audio is what matters. */
      }
    }

    const saved: SavedMeta = {
      take_id: takeId,
      version_id: meta?.version_id ?? info.version_id,
      song_id: meta?.song_id ?? null,
      song_title: meta?.song_title ?? info.filename,
      name: meta?.name ?? '',
      duration_s: meta?.duration_s ?? info.duration_s,
      bytes: total > 0 ? total : (meta?.bytes ?? info.bytes),
      saved_at: Math.floor(Date.now() / 1000),
    };
    await cache.put(
      metaKey(takeId),
      new Response(JSON.stringify(saved), {
        headers: { 'content-type': 'application/json' },
      }),
    );

    onProgress?.(100);
  } catch (e) {
    // A half-written entry is worse than none: it would report as "downloaded"
    // and then fail at play time. cache.put() is atomic, but the peaks/meta
    // writes are not, so sweep anything that landed.
    try {
      const cache = await caches.open(CACHE);
      await cache.delete(audioKey(takeId));
    } catch {
      /* nothing to clean up */
    }
    throw e;
  } finally {
    inflight.delete(takeId);
  }
}

/** Take it back off the phone. The bytes in R2 are untouched — this is a phone
 *  eviction, not a deletion. Nothing is ever deleted. (ADR-010) */
export async function removeTake(takeId: string): Promise<void> {
  if (!cacheOk()) return;
  revokeBlobUrl(takeId);
  const cache = await caches.open(CACHE);
  await Promise.all([
    cache.delete(audioKey(takeId)),
    cache.delete(peaksKey(takeId)),
    cache.delete(metaKey(takeId)),
  ]);
}

async function savedMeta(takeId: string): Promise<SavedMeta | null> {
  if (!cacheOk()) return null;
  try {
    const cache = await caches.open(CACHE);
    const hit = await cache.match(metaKey(takeId));
    return hit ? ((await hit.json()) as SavedMeta) : null;
  } catch {
    return null;
  }
}

/* ----------------------------------------------------------------- storage */

export interface StorageReport {
  /** how many takes are on the phone */
  count: number;
  /** how many bytes WE are using, from the cached responses themselves */
  saved: number;
  /** the browser's own numbers. null on a browser that won't say. */
  usage: number | null;
  quota: number | null;
  free: number | null;
  /** did the browser agree not to evict us? Usually not. Never assume it did. */
  persisted: boolean;
}

/**
 * Ask the browser to keep our data. It is allowed to refuse, and Safari usually
 * does unless the app has been added to the home screen. We ask once, we take
 * the answer, and we never build anything on top of the assumption that it said
 * yes — see the eviction note at the top of the file.
 */
async function askToPersist(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function storageReport(): Promise<StorageReport> {
  const ids = await downloadedTakeIds();
  let saved = 0;

  if (cacheOk()) {
    const cache = await caches.open(CACHE);
    for (const id of ids) {
      const hit = await cache.match(audioKey(id));
      if (!hit) continue;
      const len = Number(hit.headers.get('content-length') ?? 0);
      // The header is the cheap way. If it isn't there, ask the blob — which
      // reads size off disk, not into memory.
      saved += len > 0 ? len : (await hit.blob()).size;
    }
  }

  let usage: number | null = null;
  let quota: number | null = null;
  try {
    // Not on older iOS. When it isn't there we show the count and say nothing
    // about gigabytes, rather than inventing a number.
    const est = await navigator.storage?.estimate?.();
    if (est) {
      usage = typeof est.usage === 'number' ? est.usage : null;
      quota = typeof est.quota === 'number' ? est.quota : null;
    }
  } catch {
    /* no estimate. Say less. */
  }

  const free = usage !== null && quota !== null ? Math.max(0, quota - usage) : null;
  let persisted = false;
  try {
    persisted = (await navigator.storage?.persisted?.()) ?? false;
  } catch {
    /* ignore */
  }

  return { count: ids.size, saved, usage, quota, free, persisted };
}

/* ------------------------------------------------------------------- words */

/** "7.2 MB". Never "7340032 bytes", never "size unknown" dressed up as 0. */
export function fmtBytes(b: number | null | undefined): string {
  if (b == null || !Number.isFinite(b) || b <= 0) return 'size unknown';
  const kb = b / 1024;
  if (kb < 900) return `${Math.max(1, Math.round(kb))} KB`;
  const mb = kb / 1024;
  if (mb < 900) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/** The size on a button, and whether we actually know it. */
function sizeWords(v: { bytes: number | null; est_bytes: number | null }): string {
  if (v.bytes != null && v.bytes > 0) return fmtBytes(v.bytes);
  if (v.est_bytes != null && v.est_bytes > 0) return `about ${fmtBytes(v.est_bytes)}`;
  return 'size unknown';
}

/** The one number that matters before a download starts. Estimates and exact
 *  sizes add up together; the wording downgrades to "about" if ANY of it was an
 *  estimate, and admits it if some of it is a complete unknown. */
function totalWords(vs: OfflineVersion[]): string {
  let total = 0;
  let estimated = false;
  let unknown = 0;

  for (const v of vs) {
    if (v.bytes != null && v.bytes > 0) total += v.bytes;
    else if (v.est_bytes != null && v.est_bytes > 0) {
      total += v.est_bytes;
      estimated = true;
    } else unknown++;
  }

  if (total === 0) return unknown ? 'size unknown' : '';
  const size = `${estimated ? 'about ' : ''}${fmtBytes(total)}`;
  return unknown ? `${size}, plus ${unknown} of unknown size` : size;
}

/* ---------------------------------------------------------------- the data */

export interface OfflineVersion {
  version_id: string;
  take_id: string;
  name: string;
  duration_s: number | null;
  bytes: number | null;
  est_bytes: number | null;
  has_peaks: boolean;
  is_canonical: boolean;
}

export interface OfflineSong {
  song_id: string;
  title: string;
  color: string | null;
  canonical_version_id: string | null;
  versions: OfflineVersion[];
}

async function getManifest(signal: AbortSignal): Promise<OfflineSong[]> {
  const res = await fetch('/api/offline/manifest', {
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw new Error('manifest');
  const j = (await res.json()) as { songs?: unknown[] };

  return ((j.songs ?? []) as Record<string, unknown>[]).map((s) => ({
    song_id: String(s.song_id ?? ''),
    title: String(s.title ?? 'Untitled'),
    color: typeof s.color === 'string' ? s.color : null,
    canonical_version_id:
      typeof s.canonical_version_id === 'string' ? s.canonical_version_id : null,
    versions: ((s.versions ?? []) as Record<string, unknown>[]).map((v) => ({
      version_id: String(v.version_id ?? ''),
      take_id: String(v.take_id ?? ''),
      name: String(v.name ?? 'A version'),
      duration_s: typeof v.duration_s === 'number' ? v.duration_s : null,
      bytes: typeof v.bytes === 'number' ? v.bytes : null,
      est_bytes: typeof v.est_bytes === 'number' ? v.est_bytes : null,
      has_peaks: v.has_peaks === true,
      is_canonical: v.is_canonical === true,
    })),
  }));
}

/**
 * The list we show when the manifest is unreachable — i.e. when he is actually
 * offline, which is precisely when this screen matters most. Built from what is
 * in the cache. It is not as pretty. It is true.
 */
async function manifestFromCache(): Promise<OfflineSong[]> {
  const ids = [...(await downloadedTakeIds())];
  const metas = await Promise.all(ids.map(savedMeta));

  const songs = new Map<string, OfflineSong>();
  ids.forEach((takeId, i) => {
    const m = metas[i];
    const songId = m?.song_id ?? m?.song_title ?? takeId;
    let song = songs.get(songId);
    if (!song) {
      song = {
        song_id: songId,
        title: m?.song_title ?? 'Saved on this phone',
        color: null,
        canonical_version_id: null,
        versions: [],
      };
      songs.set(songId, song);
    }
    song.versions.push({
      version_id: m?.version_id ?? '',
      take_id: takeId,
      name: m?.name || 'A version',
      duration_s: m?.duration_s ?? null,
      bytes: m?.bytes ?? null,
      est_bytes: m?.bytes ?? null,
      has_peaks: false,
      is_canonical: false,
    });
  });

  return [...songs.values()];
}

/* -------------------------------------------------------------- the screen */

/** Cellular, as far as the phone will admit. `connection.type` is the only
 *  honest signal — effectiveType tells you how FAST it is, not who is paying
 *  for it — and Safari implements neither, so most of the time this is false
 *  and we simply do not nag. saveData is a person who has already told their
 *  phone they are counting megabytes; believe them. */
function onCellular(): boolean {
  const c = (navigator as Navigator & { connection?: { type?: string; saveData?: boolean } })
    .connection;
  if (!c) return false;
  return c.type === 'cellular' || c.saveData === true;
}

interface Row {
  el: HTMLElement;
  paint: (state: 'idle' | 'working' | 'done', pct?: number) => void;
}

export function mountOffline(root: HTMLElement): () => void {
  const ac = new AbortController();
  const rows = new Map<string, Row>();
  let songs: OfflineSong[] = [];
  let have = new Set<string>();
  /** the "download everything" run — the reason this screen exists */
  let batchRunning = false;
  let batchCancelled = false;

  clear(root);

  /* ---- chrome ---------------------------------------------------------- */

  const title = h('h1', { text: 'Take it with you' });
  const topbar = h('header', { class: 'topbar' }, [title]);

  const banner = h('div', { class: 'offline-strip', hidden: true }, [
    h('span', { html: icon.cloud }),
    h('span', { text: "You're offline. You can still play anything you've downloaded." }),
  ]);

  const lede = h('p', {
    class: 'dl-lede',
    text: 'Save songs onto this phone so they play with no signal at all — in the car, up north, on a plane.',
  });

  const storageLine = h('p', { class: 'dl-storage', text: '' });

  const allBtn = h('button', { class: 'dl-all', type: 'button' });
  const allNote = h('p', { class: 'dl-all-note', text: '' });

  const list = h('div', { class: 'dl-songs' });

  root.append(topbar, banner, lede, allBtn, allNote, storageLine, list);

  /* ---- state → screen -------------------------------------------------- */

  function paintNet(): void {
    banner.hidden = navigator.onLine;
  }

  async function paintStorage(): Promise<void> {
    const s = await storageReport();

    if (s.count === 0) {
      storageLine.textContent = 'Nothing saved on this phone yet.';
      return;
    }

    // The honest version. If the browser won't tell us how much room is left,
    // we do not make a number up — we just say what we know.
    const saved = `You've saved ${fmtBytes(s.saved)} — ${s.count} ${s.count === 1 ? 'song' : 'songs'}.`;
    storageLine.textContent =
      s.free !== null ? `${saved} Your phone has ${fmtBytes(s.free)} free.` : saved;
  }

  /** The canonical versions that aren't on the phone yet. The one-tap feature. */
  function missingCanonical(): { song: OfflineSong; version: OfflineVersion }[] {
    const out: { song: OfflineSong; version: OfflineVersion }[] = [];
    for (const song of songs) {
      const v = song.versions.find((x) => x.is_canonical) ?? song.versions[0];
      if (v && !have.has(v.take_id)) out.push({ song, version: v });
    }
    return out;
  }

  function paintAllButton(): void {
    if (batchRunning) {
      allBtn.textContent = 'Stop';
      allBtn.dataset.stop = '1';
      allBtn.disabled = false;
      return;
    }
    delete allBtn.dataset.stop;

    const missing = missingCanonical();
    if (songs.length === 0) {
      allBtn.hidden = true;
      allNote.hidden = true;
      return;
    }

    allBtn.hidden = false;
    if (missing.length === 0) {
      allBtn.textContent = 'Every song is on your phone';
      allBtn.disabled = true;
      allNote.hidden = true;
      return;
    }

    allBtn.disabled = false;
    allBtn.textContent = `Download all ${missing.length} songs`;
    allNote.hidden = false;
    const size = totalWords(missing.map((m) => m.version));
    allNote.textContent = size
      ? `The best version of each song. ${size}.`
      : 'The best version of each song.';
  }

  /* ---- one row --------------------------------------------------------- */

  function buildRow(song: OfflineSong, version: OfflineVersion): Row {
    const name = h('span', { class: 'txt', text: version.name });
    const star = version.is_canonical
      ? h('span', { class: 'star', html: icon.star, title: 'The one' })
      : null;

    const meta = h('span', {
      class: 'dl-meta',
      text: `${fmtTime(version.duration_s)} · ${sizeWords(version)}`,
    });

    const fill = h('i');
    const bar = h('div', { class: 'bar', hidden: true }, [fill]);
    const btn = h('button', { class: 'dl-btn', type: 'button' });

    const el = h('div', { class: 'dl-row', 'data-state': 'idle' }, [
      h('div', { class: 'dl-main' }, [h('span', { class: 'dl-name' }, [star, name]), meta]),
      btn,
      bar,
    ]);

    const paint = (state: 'idle' | 'working' | 'done', pct = 0) => {
      el.dataset.state = state;
      bar.hidden = state !== 'working';
      fill.style.width = `${pct}%`;

      if (state === 'working') {
        btn.textContent = pct > 0 ? `${pct}%` : 'Saving…';
        btn.setAttribute('aria-label', 'Cancel this download');
      } else if (state === 'done') {
        btn.innerHTML = '';
        btn.append(h('span', { class: 'tick', text: '✓' }), h('span', { text: 'Remove' }));
        btn.setAttribute('aria-label', `Remove ${version.name} from this phone`);
      } else {
        btn.textContent = 'Save';
        btn.setAttribute('aria-label', `Save ${version.name} to this phone`);
      }
    };

    btn.addEventListener('click', () => {
      const state = el.dataset.state;
      if (state === 'working') {
        cancelDownload(version.take_id);
        return;
      }
      if (state === 'done') {
        void remove(version);
        return;
      }
      void saveOne(song, version).catch(() => undefined);
    });

    paint('idle');
    return { el, paint };
  }

  function render(): void {
    clear(list);
    rows.clear();

    if (songs.length === 0) {
      list.append(
        h('p', {
          class: 'dl-empty',
          text: navigator.onLine
            ? "There's nothing to download yet. Once the band saves a version of a song, it turns up here."
            : "You're offline and nothing is saved on this phone yet.",
        }),
      );
      paintAllButton();
      return;
    }

    for (const song of songs) {
      const block = h('section', { class: 'dl-song' }, [
        h('h2', { text: song.title, style: song.color ? `--song: ${song.color}` : null }),
      ]);

      for (const v of song.versions) {
        const row = buildRow(song, v);
        rows.set(v.take_id, row);
        row.paint(have.has(v.take_id) ? 'done' : 'idle');
        block.append(row.el);
      }

      list.append(block);
    }

    paintAllButton();
  }

  /* ---- actions --------------------------------------------------------- */

  async function saveOne(song: OfflineSong, version: OfflineVersion): Promise<void> {
    const row = rows.get(version.take_id);
    row?.paint('working', 0);

    try {
      await downloadTake(version.take_id, (pct) => row?.paint('working', pct), {
        version_id: version.version_id,
        song_id: song.song_id,
        song_title: song.title,
        name: version.name,
        duration_s: version.duration_s,
        bytes: version.bytes,
      });

      have.add(version.take_id);
      row?.paint('done');
      void paintStorage();
      paintAllButton();
    } catch (e) {
      row?.paint('idle');
      // An abort is the user pressing Stop. That is not an error and it does
      // not get a red toast.
      if ((e as Error)?.name === 'AbortError') throw e;
      toast(
        navigator.onLine
          ? "Couldn't save that one. Try again?"
          : "You're offline — you'll need signal to save a song.",
        true,
      );
      throw e;
    }
  }

  async function remove(version: OfflineVersion): Promise<void> {
    await removeTake(version.take_id);
    have.delete(version.take_id);
    rows.get(version.take_id)?.paint('idle');
    void paintStorage();
    paintAllButton();
    toast('Removed from this phone. It’s still in the vault.');
  }

  /**
   * THE BUTTON HE ACTUALLY WANTS: one tap before a camping trip.
   *
   * We say the size out loud first, and we let him say no. On cellular we say
   * so — and then we do exactly what he tells us to, because it is his phone,
   * his data plan, and his band. We never block it.
   */
  function downloadAll(): void {
    const missing = missingCanonical();
    if (missing.length === 0) return;

    const size = totalWords(missing.map((m) => m.version));
    const cell = onCellular();

    const question = cell
      ? `That's ${size}. On cellular?`
      : `${missing.length} songs, ${size}. Download them now?`;

    sheet(question, [
      {
        label: cell ? 'Download anyway' : `Download all ${missing.length}`,
        icon: icon.cloud,
        accent: true,
        onSelect: () => void runBatch(missing),
      },
      { label: 'Not now', onSelect: () => undefined },
    ]);
  }

  async function runBatch(queue: { song: OfflineSong; version: OfflineVersion }[]): Promise<void> {
    batchRunning = true;
    batchCancelled = false;
    paintAllButton();

    let done = 0;
    for (const { song, version } of queue) {
      if (batchCancelled) break;
      try {
        await saveOne(song, version);
        done++;
      } catch {
        // One failure must not abandon the other eight. A phone that drops a
        // packet halfway through song three should still come home with songs
        // four through nine.
        if (batchCancelled) break;
      }
    }

    batchRunning = false;
    paintAllButton();

    if (batchCancelled) {
      toast(done ? `Stopped. ${done} saved.` : 'Stopped.');
    } else if (done === queue.length) {
      toast(`Saved. ${done} ${done === 1 ? 'song' : 'songs'} will play with no signal.`);
    } else if (done > 0) {
      toast(`Saved ${done} of ${queue.length}. Try the rest when you have signal.`, true);
    }
  }

  allBtn.addEventListener('click', () => {
    if (batchRunning) {
      batchCancelled = true;
      cancelAllDownloads();
      return;
    }
    downloadAll();
  });

  /* ---- boot ------------------------------------------------------------ */

  const onNet = () => paintNet();
  window.addEventListener('online', onNet);
  window.addEventListener('offline', onNet);
  paintNet();

  void (async () => {
    // Ask to be exempt from eviction. Safari will probably say no. Nothing here
    // depends on it saying yes.
    void askToPersist();

    have = await downloadedTakeIds();

    try {
      songs = await getManifest(ac.signal);
    } catch {
      if (ac.signal.aborted) return;
      // No manifest means no network — which is exactly when he wants to know
      // what he's already got. Show that instead of an error.
      songs = await manifestFromCache();
      if (navigator.onLine && songs.length === 0) {
        toast("Couldn't reach the vault. Try again?", true);
      }
    }

    if (ac.signal.aborted) return;
    render();
    void paintStorage();
  })();

  /* ---- teardown -------------------------------------------------------- */

  return () => {
    ac.abort();
    window.removeEventListener('online', onNet);
    window.removeEventListener('offline', onNet);
    // Deliberately NOT cancelling in-flight downloads. He tapped "download all"
    // and walked back to the shelf to look at something; the downloads should
    // still be there when he comes back. They write straight into the cache and
    // do not need this screen to be alive. Only Stop stops them.
  };
}
